// Motion for ATT Makes: scroll-reveal, scroll-driven spinning
// decorations, a page scroll progress bar, and a cart badge pop.
// Everything here is skipped when the visitor prefers reduced
// motion, and the site is fully usable if this script never runs.
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // ---- Scroll-reveal: elements fade and rise into view ----
  const targets = document.querySelectorAll(
    [
      '.product-card',
      '.why-section__item',
      '.process-steps__step',
      '.hero__text',
      '.hero__media',
      '.about-studio__text',
      '.about-studio__media',
      '.newsletter__inner',
      '.custom-callout__inner',
      '.order-form__inner',
      '.collection-card',
    ].join(', ')
  );

  if (targets.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach((el) => {
      // Stagger siblings so grids cascade instead of popping in at once
      const siblings = el.parentElement ? [...el.parentElement.children] : [el];
      const index = Math.max(siblings.indexOf(el), 0);
      el.style.transitionDelay = `${Math.min(index % 8, 5) * 70}ms`;
      el.classList.add('reveal');

      // Content already on screen at load snaps in instantly, so the
      // top of the page never looks washed out while reveals catch up
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92) {
        el.style.transition = 'none';
        el.classList.add('is-in');
        requestAnimationFrame(() => {
          el.style.transition = '';
        });
      } else {
        observer.observe(el);
      }
    });
  }

  // ---- Scroll-driven motion: progress bar + spinning decorations ----
  // Elements with data-spin rotate as the page scrolls; the value is
  // degrees per pixel scrolled (e.g. data-spin="0.06").
  const spinners = document.querySelectorAll('[data-spin]');
  const progressBar = document.querySelector('[data-scroll-progress]');

  // Hide the sticky header while actively scrolling down; bring it
  // back when scrolling stops or the visitor scrolls up.
  const headerEl = document.querySelector('header');
  const headerSection = headerEl ? headerEl.closest('.shopify-section') : null;
  let lastY = window.scrollY;
  let settleTimer = null;
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      if (progressBar) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        progressBar.style.width = max > 0 ? `${(y / max) * 100}%` : '0%';
      }
      spinners.forEach((el) => {
        const speed = parseFloat(el.dataset.spin) || 0.05;
        el.style.transform = `rotate(${y * speed}deg)`;
      });

      if (headerSection) {
        const scrollingDown = y > lastY;
        if (scrollingDown && y > 120) {
          headerSection.classList.add('is-hidden');
        } else {
          headerSection.classList.remove('is-hidden');
        }
        // Reappear shortly after scrolling stops
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          headerSection.classList.remove('is-hidden');
        }, 220);
        lastY = y;
      }

      ticking = false;
    });
  };

  if (spinners.length || progressBar || headerSection) {
    document.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---- Print reveal: the model "prints" from the bottom up as the
  // visitor scrolls through the section ----
  const printSection = document.querySelector('[data-print-section]');
  if (printSection) {
    const clip = printSection.querySelector('[data-print-clip]');
    const gantry = printSection.querySelector('[data-print-gantry]');
    const nozzle = printSection.querySelector('[data-print-nozzle]');
    const readout = printSection.querySelector('[data-print-readout]');
    const bar = printSection.querySelector('[data-print-bar]');
    const TOTAL_LAYERS = 224;

    const updatePrint = () => {
      const rect = printSection.getBoundingClientRect();
      const range = rect.height - window.innerHeight;
      const progress = Math.min(1, Math.max(0, -rect.top / (range || 1)));
      if (clip) {
        clip.style.clipPath = `inset(${(1 - progress) * 100}% 0 0 0)`;
      }
      if (gantry) {
        gantry.style.top = `${(1 - progress) * 100}%`;
        gantry.style.opacity = progress > 0.02 && progress < 0.99 ? '1' : '0';
      }
      if (nozzle) {
        // The carriage shuttles back and forth as layers build
        const travel = 50 + Math.sin(progress * 60) * 32;
        nozzle.style.left = `${travel}%`;
      }
      if (bar) {
        bar.style.width = `${progress * 100}%`;
      }
      if (readout) {
        const layer = Math.round(progress * TOTAL_LAYERS);
        const done = progress >= 1;
        readout.textContent = done
          ? `LAYER ${TOTAL_LAYERS}/${TOTAL_LAYERS} — COMPLETE — COOLING BED`
          : `LAYER ${String(layer).padStart(3, '0')}/${TOTAL_LAYERS} — ${Math.round(progress * 100)}% — NOZZLE 220°C · BED 65°C`;
      }
    };

    document.addEventListener('scroll', () => requestAnimationFrame(updatePrint), { passive: true });
    updatePrint();
  }

  // ---- Cart badge pop: bounce the header count when it changes ----
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    new MutationObserver(() => {
      el.classList.remove('pop');
      // Force a reflow so re-adding the class restarts the animation
      void el.offsetWidth;
      el.classList.add('pop');
    }).observe(el, { childList: true, characterData: true, subtree: true });
  });

  // ---- Glass tilt: cards lean very slightly toward the cursor ----
  const MAX_TILT = 3; // degrees — subtle on purpose
  document.querySelectorAll('.product-card, .spotlight__media').forEach((card) => {
    let raf = 0;
    card.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(700px) rotateX(${(-py * MAX_TILT).toFixed(2)}deg) rotateY(${(px * MAX_TILT).toFixed(2)}deg)`;
      });
    });
    card.addEventListener('pointerleave', () => {
      cancelAnimationFrame(raf);
      card.style.transition = 'transform 0.35s ease';
      card.style.transform = '';
      setTimeout(() => { card.style.transition = ''; }, 380);
    });
  });

  // ---- Product card media preview: play muted product videos on hover/focus ----
  document.querySelectorAll('video.product-card__asset--preview').forEach((video) => {
    const card = video.closest('.product-card');
    if (!card) return;

    const previewStart = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return 0;
      return Math.min(video.duration * 0.55, Math.max(video.duration - 0.25, 0));
    };
    const cuePreview = () => {
      const start = previewStart();
      if (Math.abs(video.currentTime - start) > 0.25) video.currentTime = start;
    };
    const playPreview = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      cuePreview();
      video.play().catch(() => {});
    };
    const resetPreview = () => {
      video.pause();
      cuePreview();
    };

    video.addEventListener('loadedmetadata', cuePreview, { once: true });
    video.addEventListener('ended', () => {
      cuePreview();
      video.play().catch(() => {});
    });
    card.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') playPreview();
    });
    card.addEventListener('pointerleave', resetPreview);
    card.addEventListener('focusin', playPreview);
    card.addEventListener('focusout', resetPreview);
  });
})();
